import React from 'react';
import { InputGroup } from 'shared-assets/components/Form';
import ModalCard, { modalSize } from 'shared-assets/components/Modal/ModalCard';
import { Grid } from '@material-ui/core';
import kfetch from '@kareo/kfetch';
import update from 'immutability-helper';
import {
  Autocomplete,
  Box,
  TextField,
  Checkbox,
  Button,
  Chip,
} from '@kareo/design-system';
import rollbar from 'shared-assets/src/errors/errors';
import { getUser } from 'shared-assets/facesheet-ui/service/accessControl/service';
import CachedDataWarning from 'shared-assets/facesheet-ui/components/Facesheet/Error/CachedDataWarning';
import { CACHED_DATA_MESSAGE } from 'shared-assets/facesheet-ui/service/patient/types';

class AssignModal extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      userList: [],
      restricting: this.props.restricted,
      nextValue: [],
      value: [],
      isEmpty: false,
      currentUser: {},
      hasProvider: false,
      autoAdd: false,
    };
    this.handleRestricting = this.handleRestricting.bind(this);
  }

  handleRestricting(e) {
    this.setState({ restricting: e.target.checked });
  }

  async componentDidMount() {
    const users = await this.props.getAllUsers();
    const chartSettings = await this.props.getChartProtectionSettings(
      this.props.patientId,
    );
    let value = [];
    let currentList;
    let currentUser = await getUser();
    const hasProvider = currentUser.roles.includes(2);
    if (!this.props.restricted && hasProvider) {
      value = [currentUser];
    }
    if (this.props.restricted) {
      let restrictedGuids = await this.props.getRestrictedList(
        this.props.patientId,
      );

      currentList = users.filter(user =>
        restrictedGuids.linkedUsers.includes(user.userGuid),
      );
      if (currentList.length === 0) {
        currentList = restrictedGuids.linkedUsers.includes(currentUser.userGuid)
          ? [currentUser]
          : [];
      }
      value = currentList;
    }

    this.setState({
      userList: update(this.state.userList, {
        $set: users,
      }),
      currentUser,
      nextValue: value,
      value,
      hasProvider,
      autoAdd: chartSettings?.practiceAccess?.autoAddClinicalAdmins,
    });
  }

  handleSubmit = () => {
    // check for empty restricted list while restricting
    if (this.state.value.length === 0 && this.state.restricting) {
      this.setState({
        isEmpty: true,
      });
      return;
    }

    let postList = { userGuids: [] };
    if (this.state.restricting) {
      postList = { userGuids: this.state.value.map(user => user.userGuid) };
    }

    const initialRestriction = this.props.restricted;
    // changing from restricted to unrestricted in webapp, don't wait for post
    if (
      this.state.restricting === !this.props.restricted &&
      !this.props.legacy
    ) {
      this.props.toggleRestriction();
    }

    const initialList = this.state.nextValue;
    this.setState({
      isEmpty: false,
      nextValue: this.state.value,
    });
    kfetch(
      `/facesheet-ui/api/Facesheet/access/users/patient/${this.props.patientId}`,
      {
        body: JSON.stringify(postList),
        method: 'PUT',
      },
    )
      .then(() => {
        // reload page if orig list includes user but new list does not

        let legacyRestrictionChange =
          this.state.restricting === !initialRestriction &&
          (this.props.legacy ||
            (this.props.modalRolePath == 'ADMIN' &&
              this.props.dataRestrictionEnabled));
        let isSelfRemove =
          this.state.hasProvider &&
          initialList.some(
            user => user.userGuid == this.state.currentUser.userGuid,
          ) &&
          !postList.userGuids.includes(this.state.currentUser.userGuid);

        let isSelfAdd =
          this.state.hasProvider &&
          !initialList.some(
            user => user.userGuid == this.state.currentUser.userGuid,
          ) &&
          postList.userGuids.includes(this.state.currentUser.userGuid);
        if (isSelfRemove || isSelfAdd || legacyRestrictionChange) {
          window.top.location.reload();
        }
      })
      .catch(error => {
        rollbar.error(error);
      });

    this.props.handleClose();
  };

  handleCancel = () => {
    this.setState({
      isEmpty: false,
      restricting: this.props.restricted,
      value: this.state.nextValue,
    });

    this.props.handleClose();
  };

  render() {
    return (
      <ModalCard
        open={this.props.open}
        header={
          <h4 style={{ marginTop: '11px', marginBottom: '11px' }}>
            Chart Access for {this.props.firstName} {this.props.lastName}
          </h4>
        }
        size={modalSize.default}
        closeIcon
        cardStyle={{
          position: 'fixed',
          left: '50%',
          top: '50%',
          transform: 'translate(-50%, -50%)',
        }}
        onClose={this.handleCancel}
        footer={
          <Grid container direction="row-reverse">
            <Grid item>
              <Button onClick={this.handleCancel} variant="text">
                Cancel
              </Button>
              <Button
                onClick={this.handleSubmit}
                color="vitality"
                disabled={
                  this.props.cachedChartAccessContext?.isCachedDataDisplayed
                }
              >
                Save
              </Button>
            </Grid>
          </Grid>
        }
      >
        <div className="modal-body" style={{ marginBottom: '60px' }}>
          <If
            condition={
              this.props.cachedChartAccessContext?.isCachedDataDisplayed
            }
          >
            <Box mb={1}>
              <CachedDataWarning
                messageType={CACHED_DATA_MESSAGE.WITH_DATE}
                lastCachedDate={
                  this.props.cachedChartAccessContext?.lastCachedDate
                }
                displayBorder
              />
            </Box>
          </If>
          <span>
            Restricted access allows you to control who can view this
            patient&apos;s clinical{' '}
            {this.props.dataRestrictionEnabled ? 'data' : 'notes'}.
          </span>
          <p style={{ margin: '8px 0px 8px 0px' }}>Chart Access:</p>
          <InputGroup
            checked={!this.state.restricting}
            label="Unrestricted"
            labelStyle={{ marginLeft: '0.3rem' }}
            type="radio"
            name="access"
            onChange={e => this.setState({ restricting: !e.target.checked })}
            disabled={
              this.props.cachedChartAccessContext?.isCachedDataDisplayed
            }
          />
          <InputGroup
            checked={this.state.restricting}
            label="Restricted"
            labelStyle={{ marginLeft: '0.3rem' }}
            type="radio"
            name="access"
            onChange={this.handleRestricting}
            disabled={
              this.props.cachedChartAccessContext?.isCachedDataDisplayed
            }
          />
          <If condition={this.state.restricting}>
            <Autocomplete
              multiple
              disableClearable
              value={this.state.value}
              renderTags={(tagValue, getTagProps) => {
                const renderedTags = tagValue.map((option, index) => {
                  const chipProps = getTagProps({ index });
                  return (
                    <Chip
                      label={`${option.firstName} ${option.lastName}`}
                      {...chipProps}
                      onDelete={option.disabled ? null : chipProps.onDelete}
                    />
                  );
                });

                return (
                  <>
                    {this.state.autoAdd && (
                      <Chip
                        label="Clinical Admins"
                        {...getTagProps({ index: 0 })}
                        onDelete={null}
                      />
                    )}
                    {renderedTags}
                  </>
                );
              }}
              disableCloseOnSelect
              options={this.state.userList}
              id="provider-search"
              getOptionLabel={user => `${user.firstName} ${user.lastName}`}
              ListboxProps={{ style: { paddingLeft: 0 } }}
              limitTags={5}
              renderOption={(user, { selected }) => (
                <>
                  <Checkbox checked={selected} />
                  {`${user.firstName} ${user.lastName}`}
                </>
              )}
              getOptionSelected={(user, value) =>
                user.userGuid === value.userGuid
              }
              onChange={(event, newValue) => {
                this.setState({
                  isEmpty: false,
                  value: [...newValue],
                });
              }}
              renderInput={params => (
                <TextField
                  {...params}
                  id="standard-error"
                  error={this.state.isEmpty}
                  helperText={
                    this.state.isEmpty ? 'At least one person is required' : ''
                  }
                  placeholder="type staff name"
                />
              )}
            />
          </If>
        </div>
      </ModalCard>
    );
  }
}

export default AssignModal;
